import { Injectable, inject } from '@angular/core';

import { environment } from 'src/environments/environment';

import { AuthService } from './auth.service';
import { CurrentUserService } from './current-user.service';

interface MockJwtClaims {
  userid: string;
  companyid: string;
  email: string;
  fullname: string;
  role: string;
  phonenumber: string;
  userOrigin: 'External' | 'Internal';
}

const DEFAULT_DEV_USER: MockJwtClaims = {
  userid: '1',
  companyid: '1',
  email: 'staff@brijjworks.dev',
  fullname: 'Dev Staff',
  role: 'consignment',
  phonenumber: '+15555550100',
  userOrigin: 'Internal'
};

/**
 * Dev-only SSO simulator. Mints a locally-signed mock JWT whose claims mirror
 * the production Company SSO response, then writes it to the same cookies the
 * real flow uses. All downstream code (`AuthService`, guards, interceptor,
 * `CurrentUserService`) is identical between dev and prod.
 *
 * Hard-fails if instantiated in a production build.
 */
@Injectable({ providedIn: 'root' })
export class DevAuthService {
  private readonly authService = inject(AuthService);
  private readonly currentUser = inject(CurrentUserService);

  constructor() {
    if (environment.production) {
      throw new Error('DevAuthService must not be used in production — Company SSO owns authentication.');
    }
  }

  /** Sign in as a default seeded staff user. */
  login(): void {
    this.authService.clearAuthData();
    const token = this.createMockJwt(DEFAULT_DEV_USER);
    this.authService.setTokens(token, `refresh-${Date.now()}`, `session-${Date.now()}`);
  }

  logout(): void {
    this.authService.clearAuthData();
    this.currentUser.clear();
  }

  private createMockJwt(claims: MockJwtClaims): string {
    const now = Math.floor(Date.now() / 1000);
    const header = { alg: 'HS256', typ: 'JWT' };
    const payload = {
      sub: claims.userid,
      'custom:userid': claims.userid,
      'custom:companyid': claims.companyid,
      'custom:email': claims.email,
      'custom:fullname': claims.fullname,
      'custom:role': [claims.role],
      'custom:phonenumber': claims.phonenumber,
      'custom:usertype': 'AppUser',
      'custom:userorigin': claims.userOrigin,
      'custom:timezonecode': 'UTC',
      iss: 'dev-auth-simulator',
      aud: 'consignment-portal',
      iat: now,
      nbf: now,
      exp: now + 24 * 60 * 60
    };
    return `${encode(header)}.${encode(payload)}.dev-signature`;
  }
}

function encode(value: object): string {
  return btoa(JSON.stringify(value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
